// Spacing constants (configurable)
const MIN_GAP_PIXELS = 8;
const MAX_GAP_PIXELS = 60; // Try 30 for more compact layout
export const PIXELS_PER_MINUTE = 0.7;

// Convert "HH:MM" to minutes since midnight
const parseHHMMToMinutes = (hhmm: string): number => {
  const [hours, minutes] = hhmm.split(":").map(Number);
  return hours * 60 + minutes;
};

// Get minutes since midnight from ISO date string
const getMinutesSinceMidnight = (time: string | Date): number => {
  const date = new Date(time);
  return date.getHours() * 60 + date.getMinutes();
};

export const getDurationMinutes = (
  startTime: string | Date | undefined,
  endTime: string | Date | undefined,
): number => {
  if (startTime === undefined || endTime === undefined) return 0;

  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end.getTime() - start.getTime();

  return Math.max(0, Math.round(diffMs / (1000 * 60)));
};

export const createGapCalculator = (workStartHHMM: string, workEndHHMM: string) => {
  const workStartMinutes = parseHHMMToMinutes(workStartHHMM);
  const workEndMinutes = parseHHMMToMinutes(workEndHHMM);

  const isWithinWorkHours = (time: string | Date): boolean => {
    const timeMinutes = getMinutesSinceMidnight(time);
    return timeMinutes >= workStartMinutes && timeMinutes <= workEndMinutes;
  };

  return (prevEndTime: string | Date, currentStartTime: string | Date): number => {
    if (isWithinWorkHours(prevEndTime) && isWithinWorkHours(currentStartTime)) {
      const gapMinutes = getDurationMinutes(prevEndTime, currentStartTime);
      return Math.min(Math.max(MIN_GAP_PIXELS, gapMinutes * PIXELS_PER_MINUTE), MAX_GAP_PIXELS);
    }
    return MIN_GAP_PIXELS;
  };
};

export const formatTimeRange = (startTime: string | Date | undefined, endTime: string | Date | undefined) => {
  if (startTime === undefined || endTime === undefined) return "";

  const start = new Date(startTime);
  const end = new Date(endTime);

  const formatTime = (date: Date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "pm" : "am";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes === 0 ? "" : `:${minutes.toString().padStart(2, "0")}`;
    return `${displayHours}${displayMinutes}${ampm}`;
  };

  return `${formatTime(start)} - ${formatTime(end)}`;
};

export const calculateDuration = (startTime: string | Date | undefined, endTime: string | Date | undefined) => {
  if (startTime === undefined || endTime === undefined) return "";

  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end.getTime() - start.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 1) {
    const minutes = Math.round(diffHours * 60);
    return `${minutes}min`;
  } else if (diffHours === Math.floor(diffHours)) {
    return `${Math.floor(diffHours)}h`;
  } else {
    const hours = Math.floor(diffHours);
    const minutes = Math.round((diffHours - hours) * 60);
    return `${hours}.${minutes}h`;
  }
};
